import React from 'react';
import { Star, Check } from 'lucide-react';
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";
import { useRouter } from "next/router";
import PujaStyleAccordion from "@/components/EnquiryContainer/PoojaStyleContainer";
import Link from "next/link";

const EnquiryHeroSection = ({ headline, subHeadline, heroImage, ctaText }) => {
    const scrollToBooking = () => {
        document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
    };

    const openWhatsApp = () => {
        const referenceText = `Reference: ${window.location.href}`;
        const message = encodeURIComponent(`Hi! I'd like to chat with a Puja Advisor about booking a ceremony.\n\n${referenceText}`);
        window.open(`https://wa.me/919869929292?text=${message}`, '_blank');
    };
    const router = useRouter();
    const { utm_term } = router?.query;
    return (
        <section className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-6 px-4 sm:px-6 py-8 sm:py-12 bg-[#FEEDD5] min-h-screen lg:min-h-[80vh]">
            {/* Content Section */}
            <div className="flex-1 text-center lg:text-left max-w-4xl">
                {/* Logo */}
                <Link href={process.env.NEXT_PUBLIC_SITE_URL} className="w-40 sm:w-48 lg:w-56 mx-auto lg:mx-0 mb-6 block relative h-16 sm:h-20 lg:h-24">
                  <ImageWrapper
                    src="/images/bmp.png"
                    alt="BookMyPooja Logo"
                    className="object-contain cursor-pointer"
                    priority={true}
                    loading="eager"
                    sizes="(max-width: 640px) 160px, (max-width: 1024px) 192px, 224px"
                  />
                </Link>

                {/* Rating */}
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-6">
                    <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-700 fill-current" />
                        ))}
                    </div>
                    <span className="text-yellow-700 font-medium text-sm">4.8★ Google Rated</span>
                </div>
                {utm_term && (
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-[#701417] mb-3 lg:mb-4 leading-tight tracking-tight text-center lg:text-left">
                        {utm_term}
                    </h1>
                )}

                {/* Main Heading */}
                <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-semibold text-[#8A1C23] mb-3 lg:mb-4 leading-snug text-center lg:text-left">
                    {headline ? (
                        headline
                    ) : (
                        <>
                            Worried About Doing the Puja Right? Let Us Handle Everything –
                            <span className="text-[#9B7C1D] block sm:inline font-semibold"> the Right Way.</span>
                        </>
                    )}
                </h2>

                {/* Subheading */}
                <p className="text-base sm:text-lg text-gray-700 mb-6 leading-relaxed max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
                    {subHeadline || "From pandit to samagri to rituals – Book Any Puja With Peace of Mind. 100% Vedic. 100% Hassle-Free."}
                </p>


                {/* Features */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 lg:mb-8 text-xs sm:text-sm font-medium">
                    <div className="flex items-center justify-center lg:justify-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>Certified Pandits (All languages)</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>All Inclusive Packages</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>Online & At-Home Options</span>
                    </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                    <button
                        onClick={scrollToBooking}
                        className="bg-[#8A1C23] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#6B1419] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                        {ctaText || "Book Your Puja"}
                    </button>
                    <button
                        onClick={openWhatsApp}
                        className="bg-[#1DA851] hover:bg-[#128C7E] text-white py-3 px-6 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-3 border-0 justify-center"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="flex-shrink-0"
                        >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516" />
                        </svg>
                        Chat with Puja Seva Team
                    </button>
                </div>
            </div>

            {/* Images Section */}
            {heroImage ? (
                <div className="flex-1 w-full max-w-xl lg:max-w-lg xl:max-w-xl relative h-64 sm:h-96 lg:h-[500px]">
                    <ImageWrapper
                        src={heroImage}
                        alt="Location Specific Hero"
                        className="rounded-xl shadow-lg object-cover"
                        priority={true}
                        loading="eager"
                    />
                </div>
            ) : (
                <PujaStyleAccordion />
            )}
        </section>
    );
};

export default EnquiryHeroSection;